/**
 * @file feed-merge.ts
 * @description Слияние ленты диалога (chat-window-design.md §3): сообщения
 * и служебные записи одним списком по времени, без повторов. Порядок — как у
 * сервера: при равном времени запись раньше сообщения.
 *
 * Лента приходит страницами (cabinet-api-contract §12, «чат-2»): последняя
 * страница при открытии, более ранние — по «Показать ранние сообщения»,
 * новые — долгим опросом. Все три пути сливаются одним правилом
 * (`mergeFeedItems`): одинаковый id не задваивается, свежая версия заменяет
 * прежнюю.
 *
 * Где начинается непрочитанное, кабинет берёт из личного счётчика сервера
 * (`unreadCount`, R4.19), а не из общей прежней отметки сообщения
 * `readByStaffAt`: прочитанное коллегой для меня остаётся новым.
 */

import type { ChatFeedItem, ChatMessageDto, ConversationEventDto } from "../../lib/api-chat";

function itemId(item: ChatFeedItem): string {
  return item.kind === "message" ? `m:${item.message.id}` : `e:${item.event.id}`;
}

function byTime(a: ChatFeedItem, b: ChatFeedItem): number {
  const t = Date.parse(a.at) - Date.parse(b.at);
  if (t !== 0) return t;
  if (a.kind !== b.kind) return a.kind === "event" ? -1 : 1;
  return 0;
}

/** Слить элементы ленты (страница, опрос, отправка) без повторов, по времени. */
export function mergeFeedItems(prev: readonly ChatFeedItem[], incoming: readonly ChatFeedItem[]): ChatFeedItem[] {
  const map = new Map<string, ChatFeedItem>();
  for (const item of prev) map.set(itemId(item), item);
  for (const item of incoming) map.set(itemId(item), item);
  return [...map.values()].sort(byTime);
}

/** Добавить новые сообщения и записи; одинаковые id заменяются свежими. */
export function mergeFeed(
  prev: readonly ChatFeedItem[],
  messages: readonly ChatMessageDto[],
  events: readonly ConversationEventDto[] = [],
): ChatFeedItem[] {
  return mergeFeedItems(prev, [
    ...messages.map((m): ChatFeedItem => ({ kind: "message", at: m.createdAt, message: m })),
    ...events.map((e): ChatFeedItem => ({ kind: "event", at: e.at, event: e })),
  ]);
}

/** Время последнего элемента — `since` для долгого опроса. */
export function lastFeedAt(items: readonly ChatFeedItem[], fallback: string): string {
  const last = items[items.length - 1];
  return last ? last.at : fallback;
}

/**
 * Первое моё непрочитанное сообщение клиента — над ним линия «Новые
 * сообщения». Мои непрочитанные — последние `unreadCount` сообщений клиента
 * (личная отметка сервера «прочитано до»); их меньше в ленте — линия над
 * самым ранним показанным.
 */
export function firstUnreadMessageId(items: readonly ChatFeedItem[], unreadCount: number): string | null {
  if (unreadCount <= 0) return null;
  const fromCustomer = items.flatMap((item) =>
    item.kind === "message" && item.message.authorType === "CUSTOMER" ? [item.message.id] : [],
  );
  if (fromCustomer.length === 0) return null;
  return fromCustomer[Math.max(0, fromCustomer.length - unreadCount)] ?? null;
}

/** Среди новых сообщений есть сообщение клиента — повод поставить «прочитано». */
export function hasCustomerMessage(messages: readonly ChatMessageDto[]): boolean {
  return messages.some((m) => m.authorType === "CUSTOMER");
}
